import axios, { AxiosInstance } from 'axios';
import { resetActivityTimer } from '../../utils/activityTracker';
import API_CONFIG from '../../config/apiConfig';

// Track if we're already redirecting to prevent loops
let isRedirecting = false;
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

// Process the queue after refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  
  failedQueue = [];
};

// Reset the flag on page load
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    isRedirecting = false;
    isRefreshing = false;
  });
}

// Function to refresh token
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const authServiceUrl = API_CONFIG.AUTH_SERVICE_URL || API_CONFIG.BASE_URL;
  
  try {
    const response = await axios.post(`${authServiceUrl}/api/v1/auth/refresh`, {
      refresh_token: refreshToken
    });
    
    const { access_token, refresh_token: newRefreshToken } = response.data.data;
    
    // Update tokens in localStorage
    localStorage.setItem('authToken', access_token);
    localStorage.setItem('token', access_token);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }
    
    // Update auth store if available
    const authStore = (window as any).__authStore?.getState();
    if (authStore?.setToken) {
      authStore.setToken(access_token);
    }
    
    return access_token;
  } catch (error) {
    // Clear tokens if refresh fails
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    throw error;
  }
};

export const createApiInstance = (baseURL?: string): AxiosInstance => {
  // Use provided baseURL or get it dynamically
  const dynamicBaseURL = baseURL || API_CONFIG.getApiUrl() || API_CONFIG.BASE_URL;
  
  const instance = axios.create({
    baseURL: dynamicBaseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Update base URL dynamically for this instance
  if (typeof window !== 'undefined' && !baseURL) {
    const updateInstanceBaseUrl = () => {
      const newBaseUrl = API_CONFIG.getApiUrl() || API_CONFIG.BASE_URL;
      if (newBaseUrl && instance.defaults.baseURL !== newBaseUrl) {
        instance.defaults.baseURL = newBaseUrl;
      }
    };
    
    updateInstanceBaseUrl();
    window.addEventListener('popstate', updateInstanceBaseUrl);
  }

  // Add request interceptor to include auth token
  instance.interceptors.request.use(
    async (config) => {
      // Don't set Content-Type for FormData - let browser handle it
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }
      
      let token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (token) {
        // Check if token is expired or about to expire (within 60 seconds)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const now = Date.now() / 1000;
          const expiresIn = payload.exp - now;
          
          // If token expires in less than 60 seconds, try to refresh
          if (payload.exp && expiresIn < 60) {
            
            // If not already refreshing, start the refresh process
            if (!isRefreshing) {
              isRefreshing = true;
              
              try {
                token = await refreshAccessToken();
                isRefreshing = false;
                processQueue(null, token);
              } catch (error) {
                isRefreshing = false;
                processQueue(error, null);
                
                // Only redirect if refresh fails
                if (!isRedirecting && window.location.pathname !== '/login') {
                  isRedirecting = true;
                  
                  // Show user-friendly message
                  const event = new CustomEvent('auth-error', { 
                    detail: { message: 'Your session has expired. Please log in again.' } 
                  });
                  window.dispatchEvent(event);
                  
                  setTimeout(() => {
                    window.location.href = '/login';
                  }, 1500);
                }
                
                return Promise.reject(error);
              }
            } else {
              // If already refreshing, queue this request
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              }).then(token => {
                config.headers.Authorization = `Bearer ${token}`;
                return config;
              });
            }
          }
        } catch (e) {
          console.error('[Axios Config] Error parsing token:', e);
        }
        
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Reset activity timer on API requests (shows user is active)
      resetActivityTimer();
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle errors
  instance.interceptors.response.use(
    (response) => {
      // Reset redirect flag on successful response
      isRedirecting = false;
      return response;
    },
    (error) => {

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const errorData = error.response.data;
        
        // Extract error message
        let message = 'An error occurred';
        let severity: 'error' | 'warning' | 'info' = 'error';
        
        if (errorData?.error?.message) {
          message = errorData.error.message;
        } else if (errorData?.message) {
          message = errorData.message;
        } else if (errorData?.error && typeof errorData.error === 'string') {
          message = errorData.error;
        }
        
        // Handle specific status codes
        if (status === 401) {
          // Unauthorized - session expired or invalid credentials
          if (!isRedirecting && window.location.pathname !== '/login') {
            message = message || 'Your session has expired. Please log in again.';
            severity = 'warning';
            
            const event = new CustomEvent('auth-error', { 
              detail: { message, severity } 
            });
            window.dispatchEvent(event);
            
            isRedirecting = true;
            
            // Clear auth state
            const authStore = (window as any).__authStore?.getState();
            if (authStore?.logout) {
              authStore.logout();
            }
            
            // Clear all auth-related storage
            localStorage.removeItem('authToken');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            
            // Delay redirect to allow message to show
            setTimeout(() => {
              window.location.href = '/login';
            }, 1500);
          }
        } else if (status === 403) {
          // Forbidden - user doesn't have permission
          message = message || 'You do not have permission to perform this action.';
          const event = new CustomEvent('app-error', { 
            detail: { message, severity: 'warning' } 
          });
          window.dispatchEvent(event);
        } else if (status === 404) {
          // Not found
          // 404 Not Found - skipping user notification
        } else if (status >= 500) {
          // Server error
          message = message || 'A server error occurred. Please try again later.';
          const event = new CustomEvent('app-error', { 
            detail: { message, severity } 
          });
          window.dispatchEvent(event);
        } else if (status >= 400) {
          // Client error - show the specific error message
          if (message && message !== 'An error occurred') {
            const event = new CustomEvent('app-error', { 
              detail: { message, severity } 
            });
            window.dispatchEvent(event);
          }
        }
      } else if (error.request) {
        // Request made but no response received
        if (error.message.includes('timeout')) {
          const event = new CustomEvent('app-error', { 
            detail: { 
              message: 'Connection timed out. Please check your network and try again.',
              severity: 'error'
            } 
          });
          window.dispatchEvent(event);
        } else if (error.message.includes('Network Error')) {
          const event = new CustomEvent('app-error', { 
            detail: { 
              message: 'Unable to connect to the server. Please check your internet connection.',
              severity: 'error'
            } 
          });
          window.dispatchEvent(event);
        }
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};